import { useState } from 'react';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import {
    Grid,
    FormControl,
    MenuItem,
    Select,
    Button,
    Paper,
    Slide,
    Snackbar,
    InputLabel,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Tabs,
    Tab,
    Box,
} from '@material-ui/core';
import { withTheme } from '@rjsf/core';
import { Theme as RJSFTheme } from '@rjsf/material-ui';
import validator from '@rjsf/validator-ajv8';
import { useApi, configApiRef, identityApiRef } from '@backstage/core-plugin-api';
import { Alert } from '@material-ui/lab';
import { entityPageTranslationRef } from '../../../translation/entity-page/entity-page';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { DynamicFormGeneratorFieldCD } from '../../../scaffolder/DynamicFormGeneratorField/DynamicFormGeneratorFieldCD';
import React from 'react';
import { EnvStatusCard } from './EnvStatusCard';
import { TemplateSelectorField } from '../../../scaffolder/TemplateSelectorField/TemplateSelectorField';
import { CdConfigurationPanel } from './CdConfigurationPanel';
import { useEditableCdConfigView } from './useEditableCdConfigView';

const Form = withTheme(RJSFTheme);

const ALL_ENVIRONMENTS = [
    // { key: 'base', label: 'Base' },
    { key: 'des', label: 'Desarrollo' },
    { key: 'int', label: 'Integración' },
    { key: 'sta', label: 'Staging' },
    { key: 'pre', label: 'Preproducción' },
    { key: 'pro', label: 'Producción' },
];

const GKE_INFRA_PROJECT_REF = 'project:default/prj0035937';

export const RepoConfigEditor = () => {
    const { entity } = useEntity();
    const config = useApi(configApiRef);
    const identityApi = useApi(identityApiRef);
    const catalogApi = useApi(catalogApiRef);
    const { t } = useTranslationRef(entityPageTranslationRef);
    const backendUrl = config.getString('backend.baseUrl');

    // Removed auto-load of CI config on mount. Now loads only when button is clicked.

    const slug = entity.metadata.annotations?.['github.com/project-slug'];
    const repositoryName = slug?.split('/')[1];

    // New state for tabs and config arrays
    const [activeTab, setActiveTab] = useState<'ci' | 'cd'>('ci');

    // Clear dynamic form and set configType when switching tabs
    const handleTabChange = (_: any, val: 'ci' | 'cd') => {
        setActiveTab(val);
        setConfigType(val);
        setSchema(null);
        setFormData(null);
    };
    const [ciConfigEnvs, setCiConfigEnvs] = useState<string[]>([]);
    const [cdConfigEnvs, setCdConfigEnvs] = useState<string[]>([]);
    const [loadingEnvs, setLoadingEnvs] = useState(true);
    const [restrictEnvironmentsForGkeProject, setRestrictEnvironmentsForGkeProject] = useState(false);

    const [isSavingConfig, setIsSavingConfig] = useState(false);

    // Fetch CI/CD config environments from backend on mount
    React.useEffect(() => {
        const fetchEnvironments = async () => {
            setLoadingEnvs(true);
            try {
                const token = (await identityApi.getCredentials()).token;
                const params = new URLSearchParams({
                    owner: 'ITACCIONA',
                    repoName: 'ats_APM0003211__devops_configuration',
                    artifactName: `${repositoryName}`,
                });
                const url = `${backendUrl}/api/devops-api-proxy/github-ci-cd-config-envs?${params.toString()}`;
                const response = await fetch(url, {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                });
                if (!response.ok) throw new Error('Failed to fetch environments');
                const data = await response.json();
                setCiConfigEnvs(Array.isArray(data.ci_config) ? data.ci_config : []);
                if (Array.isArray(data.cd_config)) {
                    const cdConfig = data.cd_config.filter((env: string) => env !== 'base');
                    setCdConfigEnvs(cdConfig);
                } else {
                    setCdConfigEnvs([]);
                }
            } catch (err) {
                showAlert('error', `Error fetching environments: ${err}`);
                setCiConfigEnvs([]);
                setCdConfigEnvs([]);
            } finally {
                setLoadingEnvs(false);
            }
        };
        fetchEnvironments();
        // Expose fetchEnvironments for external use
        (window as any).fetchEnvironments = fetchEnvironments;
    }, [backendUrl, identityApi]);

    React.useEffect(() => {
        const resolveEnvironmentRestriction = async () => {
            const entitySystem = entity?.spec?.system as string | undefined;
            if (!entitySystem) {
                setRestrictEnvironmentsForGkeProject(false);
                return;
            }

            try {
                const projectEntity = await catalogApi.getEntityByRef(GKE_INFRA_PROJECT_REF);
                const apps: string[] = (projectEntity?.spec as any)?.applications ?? [];
                setRestrictEnvironmentsForGkeProject(apps.includes(entitySystem));
            } catch {
                setRestrictEnvironmentsForGkeProject(false);
            }
        };

        resolveEnvironmentRestriction();
    }, [catalogApi, entity?.spec?.system]);

    // Get template name and version from annotations
    const ciTemplate = entity.metadata.annotations?.['github.com/ci-template'] || '-';
    // const cdTemplate = entity.metadata.annotations?.['github.com/cd-template'] || '-';

    const [environment, setEnvironment] = useState<string>('des');
    // Entorno sobre el que se está añadiendo el template CD
    const [cdTemplateTargetEnv, setCdTemplateTargetEnv] = useState<string | null>(null);
    const [configType, setConfigType] = useState<string>('ci');
    const [schema, setSchema] = useState<any>(null);
    const [formData, setFormData] = useState<any>(null);

    const [alertInfo, setAlertInfo] = useState<{
        type: 'warning' | 'success' | 'error' | 'info';
        message: string;
    } | null>(null);

    const [snackbarOpen, setSnackbarOpen] = useState(false);

    const allowedEnvironments = restrictEnvironmentsForGkeProject
        ? ALL_ENVIRONMENTS.filter(env => env.key === 'des' || env.key === 'pro')
        : ALL_ENVIRONMENTS;

    const allowedEnvironmentKeys = new Set(allowedEnvironments.map(env => env.key));
    const visibleCdConfigEnvs = cdConfigEnvs.filter(env => allowedEnvironmentKeys.has(env));

    // Modal state for Add CD Template
    const [cdTemplateModalOpen, setCdTemplateModalOpen] = useState(false);
    // State to hold the entity for the modal
    const [modalEntity, setModalEntity] = useState<any>(null);

    // The "Add CD Template" modal's contents (infra-driven vs. manual
    // configuration, and the switch between them) are fully owned by
    // CdConfigurationPanel — see that component. RepoConfigEditor only keeps
    // the cross-cutting state below (open/close, alerts, environment).


    const [ciTemplateModalOpen, setCiTemplateModalOpen] = useState(false);
    const [ciTemplateSelectorValue, setCiTemplateSelectorValue] = useState<any>(null);
    const [ciFormPayload, setCiFormPayload] = useState<any>(null);

    const handleOpenCiTemplateModal = () => {
        setModalEntity(entity);
        setCiTemplateModalOpen(true);
    };

    const handleCloseCiTemplateModal = () => {
        setCiTemplateModalOpen(false);
    };

    const handleSubmitCiTemplate = async () => {
        if (!repositoryName || !ciFormPayload) {
            showAlert('error', 'Repository or form data missing');
            return;
        }

        showAlert('info', 'Saving CI template configuration...');

        try {
            const payload = {
                cdFormPayload: ciFormPayload,
                entity: modalEntity,
            };

            const token = (await identityApi.getCredentials()).token;

            const url = `${backendUrl}/api/devops-api-proxy/repositories/${repositoryName}/templates`;

            const response = await fetch(url, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to save CI template config');
            }

            const entityRef = `${entity.kind.toLowerCase()}:${entity.metadata.namespace || 'default'}/${entity.metadata.name}`;
            await catalogApi.refreshEntity(entityRef);

            showAlert('success', 'CI Template configuration saved');

            handleCloseCiTemplateModal();

            setTimeout(() => {
                window.location.reload();
            }, 5000);

        } catch (err) {
            showAlert('error', `Error: ${err}`);
        }
    };

    const handleOpenCdTemplateModal = (env?: string) => {
        setModalEntity(entity);
        setCdTemplateTargetEnv(env ?? null);
        setCdTemplateModalOpen(true);
    };
    const handleCloseCdTemplateModal = () => setCdTemplateModalOpen(false);

    // Common post-save steps for both configuration modes of
    // CdConfigurationPanel (infra-driven and manual): refresh the entity so
    // the catalog reflects the new annotations, notify the user, close the
    // modal and reload the environments list + page.
    const handleCdSaved = async () => {
        const entityRef = `${entity.kind.toLowerCase()}:${entity.metadata.namespace || 'default'}/${entity.metadata.name}`;
        await catalogApi.refreshEntity(entityRef);
        showAlert('success', 'CD Template configuration saved');
        handleCloseCdTemplateModal();
        if ((window as any).fetchEnvironments) {
            (window as any).fetchEnvironments();
        }
        setTimeout(() => {
            window.location.reload();
        }, 5000);
    };

    function SlideTransition(props: any) {
        return <Slide {...props} direction="up" />;
    };

    const showAlert = (
        type: 'warning' | 'success' | 'error' | 'info',
        message: string,
    ) => {
        setSnackbarOpen(false);      // cerrar inmediatamente
        setAlertInfo(null);          // resetear estado

        // reabrir en el siguiente ciclo de render
        setTimeout(() => {
            setAlertInfo({ type, message });
            setSnackbarOpen(true);
        }, 50);
    };

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    // CD tab: dedicated facade that only shows/edits the fields the user had
    // available to configure (see useEditableCdConfigView.ts). CI keeps the
    // original generic schema/formData/fetchConfig/saveConfig below,
    // unaffected by this.
    const cdConfigView = useEditableCdConfigView({
        repositoryName,
        environment,
        onAlert: showAlert,
    });

    const buildUrl = () => {
        let url;
        if (configType === 'ci') {
            // For CI, ignore environment
            url = `${backendUrl}/api/devops-api-proxy/repo-config/${repositoryName}/${configType}`;
        } else {
            if (!environment || environment === 'base') {
                // For CD, include environment if set
                url = `${backendUrl}/api/devops-api-proxy/repo-config/${repositoryName}/${configType}`;
            } else {
                url = `${backendUrl}/api/devops-api-proxy/repo-config/${repositoryName}/${configType}?environment=${environment}`;
            }
        }
        return url;
    };

    const fetchConfig = async () => {
        showAlert('info', t('cicdContent.loadingConfigMessage'));

        try {
            const token = (await identityApi.getCredentials()).token;

            const response = await fetch(buildUrl(), {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(`${data.error}`);
            }

            setSchema(data.repositoryTemplate.properties.configValues);
            setFormData(data.values);

            showAlert('success', t('cicdContent.successLoadConfigMessage'));
            setTimeout(() => {
                setSnackbarOpen(false);
            }, 5000);
        } catch (err) {
            if (schema != null && err == 'Error: API error: Configuration not found.') {
                showAlert('warning', t('cicdContent.noConfigExistsMessage'));
                setFormData(null); // clear dynamic form content
                setSchema(null);   // also clear schema to hide the form
                setTimeout(() => {
                    setSnackbarOpen(false);
                }, 10000);
            } else {
                showAlert('error', `${err}`);
            }
        }
    };

    const saveConfig = async () => {
        if (isSavingConfig) return;

        setIsSavingConfig(true);
        showAlert('info', t('cicdContent.loadingSaveConfigMessage'));

        try {
            const token = (await identityApi.getCredentials()).token;

            const response = await fetch(buildUrl(), {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(formData),
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(`${data.error}`);
            }

            showAlert('success', t('cicdContent.successSaveConfigMessage'));
            setTimeout(() => {
                setSnackbarOpen(false);
            }, 5000);
        } catch (err) {
            showAlert('error', `${err}`);
        } finally {
            setIsSavingConfig(false);
        }
    };

    // UI for tabs
    return (
        <Grid container spacing={3} style={{ padding: 24 }}>

            {alertInfo && snackbarOpen && (
                <Snackbar
                    open={snackbarOpen}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    TransitionComponent={SlideTransition}
                >
                    <Alert onClose={handleCloseSnackbar} severity={alertInfo.type} variant="filled">
                        {alertInfo.message}
                    </Alert>
                </Snackbar>
            )}

            {/* Tabs for CI/CD */}
            <Grid item xs={12}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <Tab label="CI" value="ci" />
                    <Tab label="CD" value="cd" />
                </Tabs>
            </Grid>

            {/* Template info label - show only in respective tab, below tab headers, smaller font */}
            {activeTab === 'ci' && (
                <Grid item xs={12}>
                    <Typography variant="subtitle2" style={{ marginBottom: 8, color: '#868686ff' }}>
                        CI Template: <b>{ciTemplate}</b>
                    </Typography>
                </Grid>
            )}

            {/* Tab content */}
            <Grid item xs={12}>
                <Box hidden={activeTab !== 'ci'}>
                    {loadingEnvs ? (
                        <Typography>{t('cicdContent.configEditorLoadingConfigMessage')}</Typography>
                    ) : ciConfigEnvs.length === 0 ? (
                        <Typography color="textSecondary">{t('cicdContent.configEditorRepoLegacyMessage')}</Typography>
                    ) : (
                        <Grid container spacing={3}>
                            {/* CI Tab: Only show config load button, no config type dropdown or add CD template button */}
                            <Grid item xs={12}>
                                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={fetchConfig}
                                        style={{ height: '56px', whiteSpace: 'nowrap' }}
                                    >
                                        {t('cicdContent.configEditorLoadConfigButton')}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleOpenCiTemplateModal}
                                        style={{ height: '56px', whiteSpace: 'nowrap' }}
                                    >
                                        Change CI Template
                                    </Button>
                                </div>

                            </Grid>

                            {/* Formulario */}
                            {schema && (
                                <Grid item xs={12}>
                                    <Paper style={{ padding: 24 }}>
                                        <Form
                                            schema={schema}
                                            formData={formData}
                                            validator={validator}
                                            onChange={e => setFormData(e.formData)}
                                        >
                                            <div style={{ display: 'none' }}>
                                                {/* Hide the submit button since scaffolder handles submission */}
                                                <button type="submit">Submit</button>
                                            </div>
                                        </Form>
                                    </Paper>

                                    <Grid container justifyContent="flex-end" style={{ marginTop: 16 }}>
                                        <Button
                                            variant="contained"
                                            size='medium'
                                            color="secondary"
                                            onClick={saveConfig}
                                            disabled={isSavingConfig}
                                        >
                                            {isSavingConfig ? 'Saving...' : t('cicdContent.configEditorSaveConfigButton')}
                                        </Button>
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                    )}
                </Box>
                <Box hidden={activeTab !== 'cd'}>
                    {restrictEnvironmentsForGkeProject && (
                        <Typography variant="body2" style={{ marginBottom: 12 }}>
                            Componente perteneciente a Digital Hub, por lo tanto, mostrando solo los entornos de Desarrollo y Produccion.
                        </Typography>
                    )}
                    {loadingEnvs ? (
                        <Typography>{t('cicdContent.configEditorLoadingConfigMessage')}</Typography>
                    ) : ciConfigEnvs.length === 0 && visibleCdConfigEnvs.length === 0 ? (
                        <>
                            <Typography color="textSecondary">{t('cicdContent.configEditorRepoLegacyMessage')}</Typography>
                        </>
                    ) : visibleCdConfigEnvs.length === 0 ? (
                        <>
                            <Grid container spacing={2} style={{ marginBottom: 20 }}>
                                {allowedEnvironments.map(env => (
                                    <Grid item xs={12} sm={6} md={4} key={env.key}>
                                        <EnvStatusCard
                                            label={env.label}
                                            configured={visibleCdConfigEnvs.includes(env.key)}
                                            onAddTemplate={() => handleOpenCdTemplateModal(env.key)}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                            <Typography color="textSecondary">{t('cicdContent.configEditorCDNotConfiguredMessage')}</Typography>
                        </>
                    ) : (
                        <>
                            <Grid container spacing={2} style={{ marginBottom: 20 }}>
                                {allowedEnvironments.map(env => (
                                    <Grid item xs={12} sm={6} md={4} key={env.key}>
                                        <EnvStatusCard
                                            label={env.label}
                                            configured={visibleCdConfigEnvs.includes(env.key)}
                                            onAddTemplate={() => handleOpenCdTemplateModal(env.key)}
                                        />
                                    </Grid>
                                ))}
                            </Grid>

                            <Grid container spacing={3}>

                                <Grid item xs={12} sm={4} md={3}>
                                    <FormControl variant="outlined" fullWidth>
                                        <InputLabel>{t('cicdContent.configEditorEnvironment')}</InputLabel>
                                        <Select
                                            displayEmpty
                                            name="environment"
                                            label={t('cicdContent.configEditorEnvironment')}
                                            value={environment}
                                            onChange={(e: any) => setEnvironment(e.target.value)}
                                        >
                                            {visibleCdConfigEnvs.map(env => {
                                                let label = env;
                                                switch (env) {
                                                    // case 'base': label = 'Base'; break;
                                                    case 'des': label = 'Desarrollo'; break;
                                                    case 'int': label = 'Integración'; break;
                                                    case 'sta': label = 'Staging'; break;
                                                    case 'pre': label = 'Preproducción'; break;
                                                    case 'pro': label = 'Producción'; break;
                                                    default: label = env;
                                                }
                                                return <MenuItem key={env} value={env}>{label}</MenuItem>;
                                            })}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Botones de acción */}
                                <Grid item xs={12} sm={4} md={3}>
                                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                        <Button
                                            variant="contained"
                                            color="secondary"
                                            onClick={cdConfigView.fetchConfig}
                                            style={{ height: '56px', whiteSpace: 'nowrap' }}
                                        >
                                            {t('cicdContent.configEditorLoadConfigButton')}
                                        </Button>
                                        {/* <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={handleOpenCdTemplateModal}
                                        style={{ height: '56px', whiteSpace: 'nowrap' }}
                                        disabled={cdConfigEnvs.length !== 0}
                                    >
                                        {t('cicdContent.configEditorAddCdTemplateButton')}
                                    </Button> */}
                                    </div>
                                </Grid>

                                {/* Aviso: esta config se creó desde infraestructura (o no declara
                                    campos editables) — ver useEditableCdConfigView.ts */}
                                {cdConfigView.mode === 'quick-only' && (
                                    <Grid item xs={12}>
                                        <Alert severity="info">
                                            Esta configuración de CD se creó a partir de una infraestructura. Se
                                            muestran únicamente los campos que configuraste entonces; el resto
                                            (proyecto, namespace, credenciales, etc.) se gestiona automáticamente
                                            a partir de la infraestructura y no es editable aquí.
                                        </Alert>
                                    </Grid>
                                )}
                                {cdConfigView.mode === 'no-editable-fields' && (
                                    <Grid item xs={12}>
                                        <Alert severity="info">
                                            Esta configuración de CD se creó a partir de una infraestructura y su
                                            plantilla no declara ningún campo editable por el usuario: no hay nada
                                            que configurar aquí.
                                        </Alert>
                                    </Grid>
                                )}
                                {cdConfigView.mode === 'full-fallback' && (
                                    <Grid item xs={12}>
                                        <Alert severity="warning">
                                            No se ha podido verificar qué campos configuraste originalmente para
                                            esta infraestructura (no se pudo resolver la plantilla). Se muestra la
                                            configuración completa; ten cuidado al editar campos que normalmente
                                            gestiona la infraestructura.
                                        </Alert>
                                    </Grid>
                                )}

                                {/* Formulario CD */}
                                {cdConfigView.schema && (
                                    <Grid item xs={12}>
                                        <Paper style={{ padding: 24 }}>
                                            <Form
                                                schema={cdConfigView.schema}
                                                formData={cdConfigView.formData}
                                                validator={validator}
                                                onChange={e => cdConfigView.setFormData(e.formData)}
                                            >
                                                <div style={{ display: 'none' }}>
                                                    {/* Hide the submit button since scaffolder handles submission */}
                                                    <button type="submit">Submit</button>
                                                </div>
                                            </Form>
                                        </Paper>

                                        <Grid container justifyContent="flex-end" style={{ marginTop: 16 }}>
                                            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                                                <Button
                                                    variant="contained"
                                                    size='medium'
                                                    color="secondary"
                                                    onClick={cdConfigView.saveConfig}
                                                    disabled={cdConfigView.isSavingConfig}
                                                >
                                                    {cdConfigView.isSavingConfig ? 'Saving...' : t('cicdContent.configEditorSaveConfigButton')}
                                                </Button>
                                                <FormControl variant="outlined" size="small" style={{ minWidth: 160 }}>
                                                    <InputLabel id="target-env-label">Target Environment</InputLabel>
                                                    <Select
                                                        labelId="target-env-label"
                                                        id="target-env-select"
                                                        value={environment}
                                                        onChange={e => setEnvironment(e.target.value as string)}
                                                        label="Target Env"
                                                    >
                                                        {allowedEnvironments.map(env => (
                                                            <MenuItem key={env.key} value={env.key}>{env.label}</MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            </div>
                                        </Grid>
                                    </Grid>
                                )}
                            </Grid>
                        </>
                    )}
                </Box>
            </Grid>

            {/* Modal for Add CD Template */}
            <Dialog open={cdTemplateModalOpen} onClose={handleCloseCdTemplateModal} maxWidth="md" fullWidth>
                <DialogTitle>{t('cicdContent.configEditorAddCdTemplateButton')}</DialogTitle>
                <Typography variant="subtitle1" style={{ marginLeft: 16, marginTop: 8, color: '#1976d2' }}>
                    Repository: <b>{repositoryName || '-'}</b>
                    {entity.metadata.name ? ` | Entity: ${entity.metadata.name}` : ''}

                </Typography>
                <DialogContent>
                    {/* Ambas vías de configuración de CD (por infraestructura y
                        manual) conviven de forma intercambiable, gestionadas
                        íntegramente por CdConfigurationPanel — decisión
                        revertida el 2026-08-26 tras haber estado restringida a
                        "solo infraestructura, sin fallback manual" para los
                        miembros de iac-mvp-access. Ver CHANGELOG.md de
                        helm-deploy-gke-plataforma. */}
                    {cdTemplateModalOpen && (
                        <CdConfigurationPanel
                            repositoryName={repositoryName}
                            entity={entity}
                            initialTargetEnv={cdTemplateTargetEnv}
                            environment={environment}
                            onEnvironmentResolved={env => {
                                setCdTemplateTargetEnv(env);
                                setEnvironment(env);
                            }}
                            onAlert={showAlert}
                            onSaved={handleCdSaved}
                        />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCdTemplateModal} color="default">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={ciTemplateModalOpen} onClose={handleCloseCiTemplateModal} maxWidth="md" fullWidth>
                <DialogTitle>Change CI Template</DialogTitle>

                <DialogContent>
                    <TemplateSelectorField
                        formData={ciTemplateSelectorValue}
                        onChange={setCiTemplateSelectorValue}
                        schema={{}}
                        uiSchema={{}}
                        required
                        idSchema={{ $id: 'ciTemplateSelector' }}
                        name="ciTemplateSelector"
                        rawErrors={[]}
                        template_type="ci" //  IMPORTANTE
                        repository={repositoryName}
                        entity={entity}
                        registry={{
                            fields: {},
                            templates: { ...(RJSFTheme.templates as any) },
                            widgets: {},
                            formContext: {},
                            rootSchema: {},
                            schemaUtils: (RJSFTheme as any).schemaUtils || {},
                            translateString: (stringKey: string) => stringKey,
                        }}
                        onBlur={() => { }}
                        onFocus={() => { }}
                        disabled={false}
                        readonly={false} />

                    {ciTemplateSelectorValue?.template && ciTemplateSelectorValue?.version && (
                        <div style={{ marginTop: 24 }}>
                            <DynamicFormGeneratorFieldCD
                                onSubmit={handleSubmitCiTemplate}
                                formData={ciFormPayload}
                                onChange={(data: any) => setCiFormPayload(data)}
                                schema={{}}
                                uiSchema={{}}
                                idSchema={{ $id: 'ciDynamicForm' }}
                                name="ciDynamicForm"
                                rawErrors={[]}
                                formContext={{
                                    formData: {
                                        templateConfig: {
                                            template: ciTemplateSelectorValue.template,
                                            version: ciTemplateSelectorValue.version,
                                            branch: ciTemplateSelectorValue.branch || '',
                                        },
                                        entity: modalEntity,
                                    },
                                }}
                                registry={{
                                    fields: {},
                                    templates: { ...(RJSFTheme.templates as any) },
                                    widgets: {},
                                    formContext: {},
                                    rootSchema: {},
                                    schemaUtils: (RJSFTheme as any).schemaUtils || {},
                                    translateString: (stringKey: string) => stringKey,
                                }}
                                onBlur={() => { }}
                                onFocus={() => { }}
                                disabled={false}
                                readonly={false} />
                        </div>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleCloseCiTemplateModal}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

        </Grid>
    );
};
